import { Divider } from '@mui/material';
import { Box, Stack } from '@mui/system';
import {
  FilterSection,
  NotificationManagerProvider,
  type NotificationManagerProviderProps,
  RowFilterConditionOrNil,
  RowsSorting,
  RowTypeBase,
  SampleDataContainer,
  SmartTableContext,
  SmartTableType,
} from '@qre-mailing/smart-table';
import { FC, memo, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getSampleByOperationIdOperationStatus } from '../../api/generated-evs/sdk.gen';
import type { UsePredefinedFiltersResult } from '../../hooks/usePredefinedFilters';
import { useSampleStatus } from '../../hooks/useSampleStatus';
import { defineStyles } from '../../theme';
import { type AppToastOptions, appToast } from '../../toast/appToast';
import { FastBulkActionsBar } from '../FastActions';
import DownloadFileButton from '../DownloadFileButton';
import { ImportSection } from '../ImportSection';
import { OperationsIndicator } from '../OperationsIndicator';
import { isEmptyFilter } from '../PredefinedFilters/predefinedFilterMerge';
import { PredefinedFilters } from '../PredefinedFilters/PredefinedFilters';
import RefreshSampleButton from '../RefreshSampleButton';
import TopBarActionsPortal from '../TopBar/TopBarActionsPortal';
import { UploadSection } from '../UploadSection';

const styles = defineStyles({
  smartTableHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    width: '100%',
  },
});

export type SampleDataTabProps<RowType extends RowTypeBase> = {
  qreId: string;
  table: SmartTableType<RowType>;
  exportSampleApiCall: (condition: RowFilterConditionOrNil, sorting: RowsSorting) => Promise<void>;
  predefinedFilters: UsePredefinedFiltersResult;
};

const SampleDataTab: FC<SampleDataTabProps<RowTypeBase>> = ({
  qreId,
  table,
  exportSampleApiCall,
  predefinedFilters,
}) => {
  const intl = useIntl();
  const onExportComplete = useCallback(() => exportSampleApiCall(null, []), [exportSampleApiCall]);
  const onExportWithFilters = useCallback(
    () => exportSampleApiCall(table.rowFilterCondition ?? null, table.sorting),
    [exportSampleApiCall, table.rowFilterCondition, table.sorting]
  );

  const onReload = useCallback(() => {
    table.resetRows();
    predefinedFilters.reloadPredefinedFilters();
  }, [table, predefinedFilters]);

  const onAutoReload = useCallback(() => {
    table.softResetRows();
    predefinedFilters.reloadPredefinedFilters();
  }, [table, predefinedFilters]);

  const tableStatus = useMemo(
    () => ({
      lastUpdated: table.lastUpdated,
      totalSize: table.totalRows,
    }),
    [table.lastUpdated, table.totalRows]
  );

  const { data: statusData } = useSampleStatus(qreId, table.rowFilterCondition, tableStatus);

  const notificationManagerProps = useMemo(
    () => ({
      operationStatusApiCall: async (operationId: string) => {
        const { data } = await getSampleByOperationIdOperationStatus({
          path: { operationId },
        });
        if (!data) {
          throw new Error('Operation status not found');
        }
        return data;
      },
      toast: ((content, options) =>
        appToast(content, options as AppToastOptions | undefined)) as NotificationManagerProviderProps['toast'],
      renderDuration: ({ formattedDuration }: { formattedDuration: string }) =>
        intl.formatMessage(
          { id: 'notification.duration', defaultMessage: 'Duration: {duration}' },
          { duration: formattedDuration }
        ),
      onRefresh: () => {
        table.invalidateTable();
      },
    }),
    [table, intl]
  );

  return (
    <SmartTableContext table={table}>
      <NotificationManagerProvider {...notificationManagerProps}>
        <Box sx={styles.smartTableHeader}>
          <Stack direction="row" gap={1} justifyContent="space-between">
            <Stack direction="column" gap={1} sx={{ minWidth: 200 }}>
              <PredefinedFilters
                predefinedFilters={predefinedFilters.predefinedFilters}
                currentFilter={table.rowFilterCondition}
                setFilter={table.setRowFilterCondition}
              />
            </Stack>
            <Stack direction="column" gap={1} minWidth={'fit-content'}>
              <ImportSection />
              <UploadSection />
            </Stack>
          </Stack>
          <Divider />
          <FilterSection />
        </Box>

        <SampleDataContainer
          tableProps={{
            allowNewColumns: true,
            inheritToastContainer: true,
            inheritInfoDialogsProvider: true,
            inheritConfirmationDialogsProvider: true,
            shownBuiltInActions: ['deleteRows', 'configureColumns', 'clearSort', 'batchEdit', 'addRow', 'maxRecords'],
            bulkActions: <FastBulkActionsBar qreId={qreId} />,
          }}
        />
        <TopBarActionsPortal>
          <DownloadFileButton
            onExportComplete={onExportComplete}
            onExportWithFilters={onExportWithFilters}
            hasActiveFilters={!isEmptyFilter(table.rowFilterCondition)}
          />
          {/* <ShowHelpPopupButton /> */}
          <RefreshSampleButton
            tableStatus={tableStatus}
            serverStatus={statusData ?? { lastUpdated: null, size: 0, totalSize: 0 }}
            onReload={onReload}
            onAutoReload={onAutoReload}
            onTotalSizeChange={predefinedFilters.reloadPredefinedFilters}
          />
          <OperationsIndicator qreId={qreId} />
        </TopBarActionsPortal>
      </NotificationManagerProvider>
    </SmartTableContext>
  );
};

export default memo(SampleDataTab);
